// src/pages/Home.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import { ArrowRight, Sparkles, FileText, Star } from 'lucide-react';

const Home = () => {
  const brandColor = '#00C9A7';        // aqua green
  const brandColorDark = '#00A88C';    // deeper aqua green
  const smokeGrey = '#6B7280';         // smoke grey

  return (
    <>
      {/* HERO SECTION */}
      <section className="min-vh-100 d-flex align-items-center" style={{ backgroundColor: '#ffffff' }}>
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              {/* Badge */}
              <div
                className="badge rounded-pill px-4 py-2 mb-4"
                style={{
                  backgroundColor: `${brandColor}20`,
                  color: brandColorDark,
                }}
              >
                <Sparkles className="me-2" size={16} />
                Trusted by 100,000+ job seekers
              </div>

              {/* Heading */}
              <h1 className="display-2 fw-bold mb-4">
                <span style={{ color: brandColor }}>Launch</span> your career
                <br />
                <span className="text-dark">Build your professional story in minutes</span>
              </h1>

              {/* Subtitle */}
              <p className="lead text-muted mb-4 fs-4">
                Create a stunning resume that stands out. Get hired faster with our modern,
                ATS-friendly templates.
              </p>

              {/* TWO BUTTONS */}
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link to="/signup">
                  <Button
                    size="lg"
                    className="px-5 py-3 btn-hero-primary"
                    style={{
                      background: `linear-gradient(135deg, ${brandColor} 0%, ${smokeGrey} 100%)`,
                      borderColor: 'transparent',
                      color: '#ffffff',
                      fontWeight: '600',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `linear-gradient(135deg, ${brandColorDark} 0%, #374151 100%)`;
                      e.currentTarget.style.color = '#ffffff';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = `linear-gradient(135deg, ${brandColor} 0%, ${smokeGrey} 100%)`;
                      e.currentTarget.style.color = '#ffffff';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    Create Resume Free <ArrowRight className="ms-2" size={20} />
                  </Button>
                </Link>

                <Link to="/templates">
                  <Button
                    variant="outline-secondary"
                    size="lg"
                    className="px-5 py-3"
                    style={{
                      borderColor: brandColor,
                      color: brandColorDark,
                      backgroundColor: 'transparent',
                      fontWeight: '600',
                      borderWidth: '2px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `linear-gradient(135deg, ${brandColor} 0%, ${smokeGrey} 100%)`;
                      e.currentTarget.style.borderColor = 'transparent';
                      e.currentTarget.style.color = '#ffffff';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = brandColor;
                      e.currentTarget.style.color = brandColorDark;
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    View Templates
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="d-flex justify-content-center gap-5 mt-5">
                <div>
                  <div className="h2 fw-bold" style={{ color: brandColor }}>39%</div>
                  <div className="text-muted small">More interviews</div>
                </div>
                <div>
                  <div className="h2 fw-bold" style={{ color: brandColor }}>70+</div>
                  <div className="text-muted small">Templates</div>
                </div>
                <div>
                  <div className="h2 fw-bold" style={{ color: brandColor }}>4.9/5</div>
                  <div className="text-muted small">User rating</div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FEATURES SECTION */}
      <Container className="py-5">
        <h2 className="text-center display-5 fw-bold mb-5">
          Why <span style={{ color: brandColorDark }}>Fast Global Resume</span>?
        </h2>
        <Row className="g-4">
          {[
            {
              icon: <FileText size={40} style={{ color: brandColor }} />,
              title: 'Professional Templates',
              desc: 'Choose from 70+ recruiter-approved, ATS-friendly templates.',
            },
            {
              icon: <Sparkles size={40} style={{ color: brandColor }} />,
              title: 'AI-Powered Content',
              desc: 'Get smart suggestions to highlight your skills and experience.',
            },
            {
              icon: <Star size={40} style={{ color: brandColor }} />,
              title: 'ATS-Friendly Format',
              desc: 'Pass applicant tracking systems with optimized resume formats.',
            },
          ].map((f, i) => (
            <Col md={4} key={i}>
              <Card className="h-100 text-center p-4 shadow-sm border-0">
                <div className="mb-3">{f.icon}</div>
                <Card.Title className="fw-bold">{f.title}</Card.Title>
                <Card.Text className="text-muted">{f.desc}</Card.Text>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      {/* CTA SECTION — aqua green → smoke gradient background with white text */}
      <section
        className="py-5"
        style={{
          background: `linear-gradient(135deg, ${brandColor} 0%, ${smokeGrey} 100%)`,
          color: '#ffffff',
        }}
      >
        <Container className="text-center py-5">
          <h2 className="display-4 fw-bold mb-3" style={{ color: '#ffffff' }}>
            Ready to Build Your Resume?
          </h2>
          <p className="lead mb-4" style={{ color: '#ffffff', opacity: 0.9 }}>
            Join 100,000+ professionals who landed their dream jobs.
          </p>

          <Link to="/signup">
            <Button
              variant="light"
              size="lg"
              className="px-5 py-3 fw-bold"
              style={{
                color: brandColorDark,
                backgroundColor: '#ffffff',
                borderColor: '#ffffff',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${brandColorDark} 0%, #374151 100%)`;
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.color = brandColorDark;
                e.currentTarget.style.borderColor = '#ffffff';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Create Resume Free <ArrowRight className="ms-2" size={20} />
            </Button>
          </Link>
        </Container>
      </section>
    </>
  );
};

export default Home;